import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import { toast } from "sonner";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { RefreshCw, Sparkles, MapPin, Phone, Mail, User as UserIcon, Home, Calendar } from "lucide-react";
import { api, formatApiError } from "../../lib/api";

/**
 * Fix default Leaflet marker icons (CRA doesn't resolve the default URLs
 * because leaflet expects them to be served relative to its own CSS file).
 */
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Custom gold-outlined marker matching the LuxStay theme
const goldIcon = L.divIcon({
  className: "lux-map-marker",
  html: `
    <div style="
      width: 30px; height: 30px; border-radius: 50% 50% 50% 0;
      background: #d4af37; border: 2px solid #041f1b;
      transform: rotate(-45deg);
      box-shadow: 0 3px 10px rgba(0,0,0,0.5);
      display: grid; place-items: center;
      pointer-events: none;
    ">
      <span style="transform: rotate(45deg); color: #041f1b; font-weight: 700; font-size: 13px; pointer-events: none;">L</span>
    </div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 30],
  popupAnchor: [0, -28],
});

function RecenterOnLoad({ items, center }) {
  const map = useMap();
  useEffect(() => {
    const pts = items.filter(i => i.lat != null && i.lng != null);
    if (pts.length === 0) {
      map.setView([center.lat, center.lng], center.zoom || 11);
      return;
    }
    if (pts.length === 1) {
      map.setView([pts[0].lat, pts[0].lng], 14);
      return;
    }
    const bounds = L.latLngBounds(pts.map(i => [i.lat, i.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
  }, [items, center, map]);
  return null;
}

export default function AdminMap() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backfilling, setBackfilling] = useState(false);

  async function load() {
    try {
      const { data } = await api.get("/admin/apartments-map");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  }

  useEffect(() => { load(); }, []);

  async function runBackfill() {
    setBackfilling(true);
    try {
      const { data: res } = await api.post("/admin/apartments-map/backfill?limit=50");
      toast.success(`Geocodificados: ${res.geocoded} · Falharam: ${res.failed}`);
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setBackfilling(false); }
  }

  if (loading) return <div className="text-[#a0b2af]">A carregar mapa…</div>;
  if (!data) return <div className="text-[#a0b2af]">Sem dados disponíveis.</div>;

  const { items, total, with_coords, without_coords, default_center } = data;
  const mapped = items.filter(i => i.lat != null && i.lng != null);
  const unmapped = items.filter(i => i.lat == null || i.lng == null);

  return (
    <div className="space-y-8" data-testid="admin-map-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow mb-3">Geografia</div>
          <h1 className="font-display text-5xl">Mapa de alojamentos</h1>
          <div className="text-sm text-[#a0b2af] mt-2 max-w-2xl">
            Todos os alojamentos dos seus clientes na Madeira. Clique num marcador para ver os dados do host,
            próxima limpeza agendada e morada.
          </div>
        </div>
        <div className="flex gap-3 items-center">
          <div className="text-xs text-[#a0b2af] text-right">
            <div><span className="text-[#d4af37] font-mono">{with_coords}</span> mapeados</div>
            <div><span className="text-[#fca5a5] font-mono">{without_coords}</span> sem morada</div>
          </div>
          {without_coords > 0 && (
            <button onClick={runBackfill} disabled={backfilling}
                    className="btn-ghost-gold px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="admin-map-backfill">
              {backfilling ? <RefreshCw size={13} className="animate-spin" /> : <Sparkles size={13} />}
              {backfilling ? "A geocodificar…" : "Geocodificar em falta"}
            </button>
          )}
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <StatCard label="Total alojamentos" value={total} />
        <StatCard label="Mapeados" value={with_coords} accent="gold" />
        <StatCard label="Sem localização" value={without_coords} accent={without_coords > 0 ? "red" : "neutral"} />
      </div>

      {/* Map — responsive height: shorter on mobile, taller on desktop */}
      <div className="lux-card overflow-hidden h-[380px] sm:h-[520px] lg:h-[620px]" data-testid="admin-map-container">
        <MapContainer center={[default_center.lat, default_center.lng]} zoom={default_center.zoom}
                      scrollWheelZoom style={{ height: "100%", width: "100%", background: "#041f1b" }}>
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          <RecenterOnLoad items={mapped} center={default_center} />
          {mapped.map(apt => (
            <Marker key={apt.id} position={[apt.lat, apt.lng]} icon={goldIcon}>
              <Popup maxWidth={320} minWidth={260} className="lux-popup">
                <MarkerPopup apt={apt} />
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* Unmapped list */}
      {unmapped.length > 0 && (
        <div className="lux-card p-5" data-testid="admin-map-unmapped-list">
          <div className="eyebrow mb-3">Alojamentos sem localização</div>
          <div className="text-xs text-[#a0b2af] mb-4">
            Estes alojamentos não puderam ser geocodificados. Edite a morada para uma versão mais completa
            (rua, número, código postal) e execute novamente a geocodificação.
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {unmapped.slice(0, 12).map(a => (
              <div key={a.id} className="p-3 border border-[#1a4640] rounded-sm text-xs">
                <div className="flex items-center justify-between">
                  <span className="text-[#d4af37] font-mono">{a.apt_type}</span>
                  <span className="text-[#a0b2af]">{a.owner?.name || "—"}</span>
                </div>
                <div className="font-display text-base mt-1">{a.name}</div>
                {a.address && <div className="text-[#a0b2af] mt-1 italic">{a.address}</div>}
                {!a.address && <div className="text-[#fca5a5] mt-1 italic">sem morada</div>}
              </div>
            ))}
          </div>
          {unmapped.length > 12 && (
            <div className="text-xs text-[#a0b2af] mt-3">
              + {unmapped.length - 12} adicionais não listados aqui.
            </div>
          )}
        </div>
      )}

      {/* Inline custom CSS for the Leaflet popup theme */}
      <style>{`
        .lux-popup .leaflet-popup-content-wrapper {
          background: #062c27; color: #fdfbf7; border: 1px solid #1a4640;
          border-radius: 2px; box-shadow: 0 10px 30px rgba(0,0,0,0.5);
        }
        .lux-popup .leaflet-popup-tip { background: #062c27; border: 1px solid #1a4640; }
        .lux-popup .leaflet-popup-content { margin: 14px 16px; font-family: "Outfit", sans-serif; }
        .lux-popup a { color: #d4af37; }
        .leaflet-container { font-family: "Outfit", sans-serif; }
      `}</style>
    </div>
  );
}

function StatCard({ label, value, accent = "neutral" }) {
  const colorMap = {
    gold: "text-[#d4af37]",
    red: "text-[#fca5a5]",
    neutral: "text-[#fdfbf7]",
  };
  return (
    <div className="lux-card p-5">
      <div className="eyebrow text-[10px]">{label}</div>
      <div className={`font-display text-4xl mt-2 ${colorMap[accent]}`}>{value}</div>
    </div>
  );
}

function MarkerPopup({ apt }) {
  return (
    <div className="space-y-3 min-w-[240px]" data-testid={`map-popup-${apt.id}`}>
      <div>
        <div style={{ color: "#d4af37", fontSize: 11, letterSpacing: 2, textTransform: "uppercase" }}>
          {apt.apt_type}
        </div>
        <div style={{ fontFamily: "Cormorant Garamond, serif", fontSize: 22, lineHeight: 1.1, marginTop: 2 }}>
          {apt.name}
        </div>
      </div>

      {apt.address && (
        <div style={{ fontSize: 12, color: "#a0b2af", display: "flex", gap: 6, alignItems: "flex-start" }}>
          <MapPin size={12} style={{ color: "#d4af37", marginTop: 2, flexShrink: 0 }} />
          <span>{apt.address}</span>
        </div>
      )}

      {apt.owner && (
        <div style={{ paddingTop: 8, borderTop: "1px solid #1a4640", fontSize: 12 }}>
          <div style={{ color: "#d4af37", fontSize: 10, letterSpacing: 2, textTransform: "uppercase", marginBottom: 4 }}>
            Cliente / Host
          </div>
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <UserIcon size={12} style={{ color: "#d4af37", flexShrink: 0 }} />
            <span style={{ fontWeight: 500 }}>{apt.owner.name}</span>
            {apt.owner.registration_number && (
              <span style={{ color: "#a0b2af", fontFamily: "monospace", fontSize: 11 }}>
                · {apt.owner.registration_number}
              </span>
            )}
          </div>
          {apt.owner.phone && (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
              <Phone size={12} style={{ color: "#d4af37", flexShrink: 0 }} />
              <a href={`tel:${apt.owner.phone}`} data-testid={`map-popup-phone-${apt.id}`}>
                {apt.owner.phone}
              </a>
            </div>
          )}
          {apt.owner.email && (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 4 }}>
              <Mail size={12} style={{ color: "#d4af37", flexShrink: 0 }} />
              <a href={`mailto:${apt.owner.email}`} style={{ wordBreak: "break-all" }}>
                {apt.owner.email}
              </a>
            </div>
          )}
        </div>
      )}

      <div style={{ paddingTop: 8, borderTop: "1px solid #1a4640", fontSize: 11, color: "#a0b2af" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          <Calendar size={12} style={{ color: "#d4af37", flexShrink: 0 }} />
          <span>
            {apt.upcoming_count > 0
              ? `${apt.upcoming_count} limpeza${apt.upcoming_count > 1 ? "s" : ""} nos próximos 30 dias`
              : "Sem limpezas agendadas"}
          </span>
        </div>
        {apt.next_booking && (
          <div style={{ marginTop: 4, color: "#d4af37", fontSize: 11 }}>
            Próxima: {apt.next_booking.scheduled_date} às {apt.next_booking.scheduled_time}
          </div>
        )}
      </div>

      <a
        href={`https://www.google.com/maps/search/?api=1&query=${apt.lat},${apt.lng}`}
        target="_blank"
        rel="noreferrer"
        data-testid={`map-popup-directions-${apt.id}`}
        style={{
          display: "inline-flex", alignItems: "center", gap: 6, marginTop: 4,
          fontSize: 11, color: "#d4af37", textDecoration: "none",
        }}
      >
        <Home size={11} /> Abrir no Google Maps
      </a>
    </div>
  );
}
